<script setup lang="ts">
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from "reka-ui"
import { Button, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"

withDefaults(
  defineProps<{
    open: boolean
    title?: string
    message?: string
    confirmText?: string
    cancelText?: string
    variant?: "primary" | "danger"
    isLoading?: boolean
  }>(),
  {
    title: undefined,
    message: undefined,
    confirmText: undefined,
    cancelText: undefined,
    variant: "primary",
    isLoading: false,
  },
)

const emit = defineEmits<{
  "update:open": [value: boolean]
  "confirm": []
  "cancel": []
}>()

const { t } = useTranslation()

function onCancel() {
  emit("cancel")
  emit("update:open", false)
}

function onConfirm() {
  emit("confirm")
}
</script>

<template>
  <DialogRoot :open="open" @update:open="(val) => !val && onCancel()">
    <DialogPortal>
      <DialogOverlay class="confirm-modal-overlay">
        <DialogContent class="confirm-modal-content" @open-auto-focus.prevent>
          <DialogTitle as="h2" class="font-heading text-xl text-primary mb-2">
            {{ title ?? t('confirm_modal.title') }}
          </DialogTitle>
          <DialogDescription as="div" class="text-sm text-gray-600 mb-6">
            <slot name="message">
              <p>{{ message ?? t('confirm_modal.message') }}</p>
            </slot>
          </DialogDescription>

          <div class="flex justify-end gap-3">
            <DialogClose as-child>
              <Button
                variant="secondary"
                :disabled="isLoading"
                data-test="cancel-button"
                @click="onCancel"
              >
                {{ cancelText ?? t('confirm_modal.cancel_text') }}
              </Button>
            </DialogClose>
            <Button
              :variant="variant"
              :disabled="isLoading"
              data-test="confirm-button"
              @click="onConfirm"
            >
              <Icon v-if="isLoading" icon="Loading" data-test="loading-icon" />
              {{ confirmText ?? t('confirm_modal.confirm_text') }}
            </Button>
          </div>
        </DialogContent>
      </DialogOverlay>
    </DialogPortal>
  </DialogRoot>
</template>

<style lang="postcss">
@reference "../../../assets/styles.css";

.confirm-modal-overlay {
  @apply fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4;
}

.confirm-modal-content {
  @apply bg-white rounded-xl shadow-lg p-6 w-full max-w-md outline-none;
}
</style>
